import { useMemo, useState } from 'react';
import { useEditor } from '../../state/store';
import { ColorMode } from '../../core/types';
import { encodeAnsi, encodeText } from '../../ansi/Encoder';
import { formatBytes, optimizeAnsi } from '../../ansi/Optimizer';
import { escapeForDisplay } from '../../ansi/SGR';
import { generateCode, CodeTarget, CODE_TARGETS } from '../../export/codegen';
import { copyToClipboard, downloadText } from '../fileIO';
import { Checkbox, Field, Modal, Segmented } from '../widgets/common';
import { Select } from '../widgets/primitives';

type Format = 'ansi' | 'text' | 'json' | 'code';

interface Preset {
  name: string;
  colorMode: ColorMode;
  note: string;
}

const PRESETS: readonly Preset[] = [
  { name: 'PuTTY compatible', colorMode: 'ansi16', note: 'ANSI 16, UTF-8 — safest over old SSH clients' },
  { name: 'IRC', colorMode: 'ansi256', note: 'ANSI 256, UTF-8' },
  { name: 'Modern SSH', colorMode: 'truecolor', note: 'TrueColor, UTF-8' },
];

export function ExportDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const store = useEditor.getState();
  const tab = store.active();

  const [format, setFormat] = useState<Format>('ansi');
  const [colorMode, setColorMode] = useState<ColorMode>(tab.doc.colorMode);
  const [clearScreen, setClearScreen] = useState(false);
  const [cursorHome, setCursorHome] = useState(false);
  const [trailingReset, setTrailingReset] = useState(true);
  const [trim, setTrim] = useState(true);
  const [crlf, setCrlf] = useState(false);
  const [optimize, setOptimize] = useState(true);
  const [includeBackground, setIncludeBackground] = useState(true);
  const [target, setTarget] = useState<CodeTarget>('typescript');

  const result = useMemo(() => {
    const grid = tab.doc.composite();
    if (format === 'text') {
      const text = encodeText(grid, crlf ? '\r\n' : '\n');
      return { text, bytes: new TextEncoder().encode(text).length, saved: 0, raw: text.length };
    }
    if (format === 'json') {
      const text = store.projectJson(true);
      return { text, bytes: new TextEncoder().encode(text).length, saved: 0, raw: text.length };
    }
    const encoded = encodeAnsi(grid, {
      colorMode,
      clearScreen,
      cursorHome,
      trailingReset,
      trimTrailingWhitespace: trim,
      lineEnding: crlf ? '\r\n' : '\n',
      background: includeBackground ? tab.doc.background : null,
    });
    const final = optimize ? optimizeAnsi(encoded.text) : null;
    const text = final ? final.text : encoded.text;
    if (format === 'code') {
      const code = generateCode(target, {
        name: tab.doc.name,
        width: tab.doc.width,
        height: tab.doc.height,
        ansi: text,
        variables: tab.doc.variables,
      });
      return { text: code, bytes: new TextEncoder().encode(code).length, saved: 0, raw: code.length };
    }
    return {
      text,
      bytes: new TextEncoder().encode(text).length,
      saved: encoded.stats.savedRatio,
      raw: encoded.stats.naiveBytes,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, colorMode, clearScreen, cursorHome, trailingReset, trim, crlf, optimize, includeBackground, target]);

  const extension =
    format === 'ansi' ? 'ans' : format === 'text' ? 'txt' : format === 'json' ? 'tui.json' : CODE_TARGETS.find((t) => t.id === target)?.extension ?? 'txt';
  const fileName = `${tab.doc.name.trim() || 'untitled'}.${extension}`;

  return (
    <Modal
      title="Export"
      onClose={onClose}
      width={760}
      footer={
        <>
          <button
            className="btn"
            onClick={async () => {
              const ok = await copyToClipboard(result.text);
              store.notify(ok ? 'Copied to clipboard.' : 'Clipboard access was blocked.', ok ? 'success' : 'warn');
            }}
          >
            Copy
          </button>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button
            className="btn primary"
            onClick={() => {
              downloadText(fileName, result.text);
              store.notify(`Exported ${fileName}`, 'success');
            }}
          >
            Download {fileName}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 14 }}>
        <div>
          <Field label="Format">
            <Segmented
              value={format}
              options={[
                { value: 'ansi' as Format, label: 'ANSI' },
                { value: 'text' as Format, label: 'Text' },
                { value: 'json' as Format, label: 'JSON' },
                { value: 'code' as Format, label: 'Code' },
              ]}
              onChange={setFormat}
            />
          </Field>

          {(format === 'ansi' || format === 'code') && (
            <>
              <Field label="Colour">
                <Segmented
                  value={colorMode}
                  options={[
                    { value: 'ansi16' as ColorMode, label: '16' },
                    { value: 'ansi256' as ColorMode, label: '256' },
                    { value: 'truecolor' as ColorMode, label: 'True' },
                  ]}
                  onChange={setColorMode}
                />
              </Field>
              <Checkbox label="Clear screen first" checked={clearScreen} onChange={setClearScreen} />
              <Checkbox label="Cursor home first" checked={cursorHome} onChange={setCursorHome} />
              <Checkbox label="Trailing reset" checked={trailingReset} onChange={setTrailingReset} />
              <Checkbox label="Trim trailing spaces" checked={trim} onChange={setTrim} />
              <Checkbox label="Include background" checked={includeBackground} onChange={setIncludeBackground} />
              <Checkbox label="Optimise escapes" checked={optimize} onChange={setOptimize} />
            </>
          )}
          <Checkbox label="CRLF line endings" checked={crlf} onChange={setCrlf} />

          {format === 'code' && (
            <Field label="Target">
              <Select
                ariaLabel="Target"
                value={target}
                options={CODE_TARGETS.map((t) => ({ value: t.id, label: t.name }))}
                onChange={setTarget}
              />
            </Field>
          )}

          <div className="faint" style={{ margin: '10px 0 6px' }}>
            Presets
          </div>
          {PRESETS.map((p) => (
            <button
              key={p.name}
              className="btn"
              style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 4 }}
              title={p.note}
              onClick={() => {
                setFormat('ansi');
                setColorMode(p.colorMode);
                setTrim(true);
                setTrailingReset(true);
              }}
            >
              {p.name}
            </button>
          ))}

          <div style={{ marginTop: 10, fontSize: 11 }} className="dim">
            <div>Size: {formatBytes(result.bytes)}</div>
            {format === 'ansi' ? (
              <>
                <div>Per-cell SGR: {formatBytes(result.raw)}</div>
                <div className="ok-text">Saved {(result.saved * 100).toFixed(1)}%</div>
              </>
            ) : null}
          </div>
        </div>

        <div>
          <div className="faint" style={{ marginBottom: 4 }}>
            Preview {format === 'ansi' ? '(escapes shown as <ESC>)' : ''}
          </div>
          <pre className="terminal-preview" style={{ maxHeight: 420, fontSize: 11 }}>
            {(format === 'ansi' ? escapeForDisplay(result.text) : result.text).slice(0, 20000)}
          </pre>
        </div>
      </div>
    </Modal>
  );
}
